import { useState } from 'react'
import {ChevronDown, BookOpen} from 'lucide-react'

interface Article {
  id: string
  title: string
  tag: string
  summary: string
  body: string[]
}

const ARTICLES: Article[] = [
  {
    id: 'aws-egress',
    title: 'How to minimize AWS egress bills',
    tag: 'AWS data transfer pricing',
    summary: 'Tactical playbook for cutting outbound transfer charges across EC2, S3 and CloudFront.',
    body: [
      'Outbound data transfer is one of the most opaque line items on an AWS invoice. Standard internet egress sits around $0.09/GB in us-east-1 and scales tier-by-tier — meaning even moderate workloads can quietly burn five figures a month.',
      'Highest-leverage tactics: route public-facing traffic through CloudFront (egress drops to ~$0.085/GB and cache hits are essentially free), use VPC Gateway Endpoints for S3 / DynamoDB to bypass NAT charges, and consolidate cross-AZ traffic by co-locating chatty services.',
      'For multi-region replication, prefer S3 Replication Time Control on a need-to-have basis and avoid replicating object versions or non-critical archive tiers. Audit egress with CUR + Athena monthly — the top-3 source ENIs typically represent 70% of the bill.',
    ],
  },
  {
    id: 'spot-vs-ondemand',
    title: 'On-Demand vs. Spot: when to switch',
    tag: 'AWS EC2 instances pricing',
    summary: 'Decision framework comparing reserved capacity, savings plans and spot interruption tolerance.',
    body: [
      'On-Demand pricing is the safest baseline but the most expensive — you pay full list rate for guaranteed capacity. Spot instances offer 60–90% discounts but can be reclaimed with a 2-minute warning, making them ideal for stateless, interruption-tolerant workloads.',
      'Rule of thumb: production stateful databases stay On-Demand or on 1-year Compute Savings Plans (~30% discount, flexible). Batch jobs, CI runners, ML training, and stateless web tiers behind autoscaling groups belong on Spot — combined with capacity-optimized allocation strategy.',
      'For long-lived workloads with predictable baselines, layer 3-year Standard RIs on top of On-Demand to maximize savings. Track spot interruption rate per instance family; m5/m6i in larger sizes typically see <5% interruption.',
    ],
  },
  {
    id: 'storage-tiers',
    title: 'Optimizing cloud storage tier thresholds',
    tag: 'Azure Cold Storage pricing',
    summary: 'When to graduate objects from Standard to Cold to Archive across AWS, Azure and GCP.',
    body: [
      'Tiering is essentially an arbitrage between storage cost and retrieval cost. Standard tiers run $0.018–$0.023/GB/month with free reads. Cold tiers (S3 IA, Azure Cool, GCP Nearline) drop to ~$0.010/GB but charge $0.01/GB on retrieval and have minimum durations.',
      'Heuristic: if an object is read less than once per quarter and is over 128 KB, move to Cold. If it has not been read in 12+ months and you can tolerate hours-long retrieval, push to Archive (S3 Glacier Deep, Azure Archive, GCP Archive) where storage drops below $0.002/GB.',
      'Automate via S3 Lifecycle Rules, Azure Lifecycle Management, or GCP Object Lifecycle. Monitor early-deletion fees: Archive tiers charge 90–180 day minimums, so churning objects in/out of Archive can cost more than just keeping them in Standard.',
    ],
  },
  {
    id: 'bigquery-cost',
    title: 'BigQuery query cost calculator essentials',
    tag: 'Google BigQuery query cost calculator',
    summary: 'Slot-based vs on-demand pricing, partition pruning, and the $5/TB query budget.',
    body: [
      'BigQuery on-demand pricing is $5 per TB scanned — sounds cheap until a single SELECT * over a 10 TB partitioned table costs $50. Always partition by ingestion date and cluster on the most-filtered columns (user_id, region, event_type).',
      'For predictable query volume above ~$2,000/mo, switch to BigQuery Editions (Standard / Enterprise / Enterprise Plus) with slot reservations. A 100-slot annual reservation is typically break-even versus on-demand at ~25 TB scanned per day.',
      'Cap costs by setting a per-query maximum_bytes_billed and a per-project daily quota. Use materialized views and BI Engine for repeat dashboards — both can collapse a $5 query down to fractions of a cent.',
    ],
  },
]

function SeoHub() {
  const [openId, setOpenId] = useState<string | null>(ARTICLES[0].id)

  return (
    <section className="rounded-2xl border border-[#1F2A44] bg-[#0E1525]/80 backdrop-blur-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-[#1F2A44] flex items-center gap-2.5">
        <BookOpen className="w-4 h-4 text-cyan-400" />
        <h3 className="text-sm font-semibold text-slate-200 tracking-tight">SEO Content Strategy Hub</h3>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-slate-500">{ARTICLES.length} guides</span>
      </div>

      <div className="divide-y divide-[#1F2A44]/60">
        {ARTICLES.map(a => {
          const open = openId === a.id
          return (
            <div key={a.id}>
              <button
                onClick={() => setOpenId(open ? null : a.id)}
                className="w-full flex items-start gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-mono uppercase tracking-wider text-cyan-400/80 bg-cyan-500/[0.06] border border-cyan-500/20 rounded px-1.5 py-0.5">
                      {a.tag}
                    </span>
                  </div>
                  <h4 className="text-[14px] font-semibold text-slate-100 tracking-tight">{a.title}</h4>
                  <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">{a.summary}</p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-slate-500 shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </button>
              {open && (
                <div className="px-5 pb-5 -mt-1 animate-float-up">
                  <div className="border-l-2 border-cyan-500/30 pl-4 space-y-3">
                    {a.body.map((p, i) => (
                      <p key={i} className="text-[13px] text-slate-300 leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default SeoHub
